import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { CheckCircle2, Send, MessageSquare, Download, AlertCircle } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function fmtDate(iso) {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleDateString("de-DE", { day: "2-digit", month: "2-digit", year: "numeric" });
  } catch { return ""; }
}
function fmtTime(iso) {
  try { return new Date(iso).toLocaleTimeString("de-DE", { hour: "2-digit", minute: "2-digit" }); }
  catch { return ""; }
}

export default function KundenFreigabe() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");
  const [autorName, setAutorName] = useState(() => localStorage.getItem("kf_autor") || "");
  const [kommentar, setKommentar] = useState("");
  const [busy, setBusy] = useState(false);
  const [showFreigabe, setShowFreigabe] = useState(false);
  const [freigabeText, setFreigabeText] = useState("");

  const load = async () => {
    try {
      const { data } = await axios.get(`${API}/public/freigabe/${token}`);
      setData(data);
    } catch (e) {
      setErr(e.response?.data?.detail || "Link ungültig oder abgelaufen.");
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [token]);

  const submitKommentar = async (e) => {
    e.preventDefault();
    const t = kommentar.trim(); if (!t) return;
    if (!autorName.trim()) { setErr("Bitte gib deinen Namen an."); return; }
    setBusy(true); setErr("");
    try {
      localStorage.setItem("kf_autor", autorName);
      await axios.post(`${API}/public/freigabe/${token}/kommentar`, { text: t, autor_name: autorName });
      setKommentar("");
      await load();
    } catch (e) { setErr(e.response?.data?.detail || "Fehler beim Absenden."); }
    finally { setBusy(false); }
  };

  const submitFreigabe = async () => {
    if (!autorName.trim()) { setErr("Bitte gib deinen Namen an."); return; }
    setBusy(true); setErr("");
    try {
      localStorage.setItem("kf_autor", autorName);
      await axios.post(`${API}/public/freigabe/${token}/freigeben`, { autor_name: autorName, kommentar: freigabeText });
      setShowFreigabe(false); setFreigabeText("");
      await load();
    } catch (e) { setErr(e.response?.data?.detail || "Fehler bei der Freigabe."); }
    finally { setBusy(false); }
  };

  if (err && !data) {
    return (
      <div className="min-h-screen bg-paper flex items-center justify-center p-6">
        <div className="paper-card p-10 max-w-md text-center">
          <AlertCircle size={40} style={{ color: "#BE2E6C" }} className="mx-auto mb-4" />
          <div className="font-archivo font-bold text-2xl mb-2">Link ungültig</div>
          <div className="text-sm text-ink/60">{err}</div>
        </div>
      </div>
    );
  }
  if (!data) {
    return <div className="min-h-screen bg-paper flex items-center justify-center mono-label">Lade Entwurf…</div>;
  }

  const { entwurf, projekt, kunde } = data;
  const isFreigegeben = entwurf.kunden_status === "freigegeben";
  const isPdf = (entwurf.datei || "").startsWith("data:application/pdf");
  const isImage = (entwurf.datei || "").startsWith("data:image/");

  return (
    <div className="min-h-screen bg-paper text-ink" data-testid="kunden-freigabe-page">
      <header className="border-b border-line bg-white">
        <div className="max-w-5xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img src="/logo-brunner.png" alt="Brunner" style={{ maxHeight: 32 }} />
          </div>
          {kunde.logo && <img src={kunde.logo} alt={kunde.firmenname} style={{ maxHeight: 32 }} />}
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-6 py-10">
        <div className="mb-8">
          <div className="mono-label">{kunde.firmenname} · {projekt.bereich}</div>
          <h1 className="font-archivo font-bold text-4xl mt-1" data-testid="kf-project-title">{projekt.titel}</h1>
          <div className="mono-label mt-2" style={{ opacity: 1 }}>
            Entwurf v{entwurf.version} · Hochgeladen von {entwurf.hochgeladen_von} · {fmtDate(entwurf.erstellt_am)}
          </div>
        </div>

        {isFreigegeben && (
          <div className="paper-card p-5 mb-6 border-l-4 flex items-center gap-3" style={{ borderLeftColor: "#157A4A" }} data-testid="kf-freigegeben-banner">
            <CheckCircle2 size={20} style={{ color: "#157A4A" }} />
            <div>
              <div className="font-medium">Freigabe erteilt durch {entwurf.kunden_freigabe_von}</div>
              <div className="mono-label mt-0.5">{fmtDate(entwurf.kunden_freigabe_am)} · {fmtTime(entwurf.kunden_freigabe_am)}</div>
            </div>
          </div>
        )}

        {entwurf.pm_notiz_an_kunde && (
          <div className="paper-card p-5 mb-6" style={{ borderLeft: "3px solid #0E62C4" }}>
            <div className="mono-label mb-2" style={{ color: "#0E62C4", opacity: 1 }}>Anmerkung des Projektmanagers</div>
            <div className="text-sm whitespace-pre-wrap">{entwurf.pm_notiz_an_kunde}</div>
          </div>
        )}

        {/* Entwurf-Ansicht */}
        <div className="paper-card p-3 mb-6" data-testid="kf-entwurf-preview">
          {isImage && <img src={entwurf.datei} alt={entwurf.dateiname} className="max-w-full mx-auto block rounded-sm" />}
          {isPdf && <iframe src={entwurf.datei} title="Entwurf" className="w-full h-[720px] border-0 rounded-sm" />}
          {!isImage && !isPdf && (
            <div className="p-8 text-center">
              <a href={entwurf.datei} download={entwurf.dateiname} className="inline-flex items-center gap-2 px-4 py-2.5 bg-ink text-white rounded-sm text-sm hover:bg-black">
                <Download size={14} /> {entwurf.dateiname}
              </a>
            </div>
          )}
        </div>

        {/* Aktionen */}
        {!isFreigegeben && (
          <div className="paper-card p-6 mb-6">
            <div className="font-archivo font-bold text-lg mb-4 flex items-center gap-2">
              <MessageSquare size={18} /> Ihre Rückmeldung
            </div>
            <div className="mb-3">
              <label className="mono-label block mb-1">Ihr Name *</label>
              <input value={autorName} onChange={(e) => setAutorName(e.target.value)} data-testid="kf-autor-name"
                className="w-full px-3 py-2 border border-line rounded-sm bg-white text-sm" placeholder="Vor- und Nachname" />
            </div>
            <form onSubmit={submitKommentar}>
              <label className="mono-label block mb-1">Kommentar oder Änderungswunsch</label>
              <textarea value={kommentar} onChange={(e) => setKommentar(e.target.value)} data-testid="kf-kommentar"
                rows={4} className="w-full px-3 py-2 border border-line rounded-sm bg-white text-sm"
                placeholder="Was möchten Sie ändern lassen?" />
              {err && <div className="text-sm mt-2" style={{ color: "#BE2E6C" }}>{err}</div>}
              <div className="flex items-center justify-between mt-4 gap-3 flex-wrap">
                <button type="submit" disabled={busy || !kommentar.trim()} data-testid="kf-send-kommentar"
                  className="inline-flex items-center gap-2 px-4 py-2.5 bg-ink text-white rounded-sm text-sm hover:bg-black disabled:opacity-50">
                  <Send size={13} /> Kommentar senden
                </button>
                <button type="button" onClick={() => setShowFreigabe(true)} data-testid="kf-open-freigabe"
                  className="inline-flex items-center gap-2 px-4 py-2.5 rounded-sm text-sm text-white hover:opacity-90"
                  style={{ backgroundColor: "#157A4A" }}>
                  <CheckCircle2 size={14} /> Entwurf freigeben
                </button>
              </div>
            </form>
          </div>
        )}

        {/* Freigabe Modal */}
        {showFreigabe && (
          <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" data-testid="kf-freigabe-modal">
            <div className="bg-white rounded-sm max-w-md w-full p-6">
              <div className="font-archivo font-bold text-xl mb-2">Entwurf freigeben?</div>
              <div className="text-sm text-ink/70 mb-4">Sie bestätigen die Freigabe des Entwurfs „{entwurf.dateiname}" (v{entwurf.version}). Dies wird an den Projektmanager gemeldet.</div>
              <label className="mono-label block mb-1">Ihr Name *</label>
              <input value={autorName} onChange={(e) => setAutorName(e.target.value)}
                className="w-full px-3 py-2 border border-line rounded-sm bg-white text-sm mb-3" placeholder="Vor- und Nachname" />
              <label className="mono-label block mb-1">Optionaler Kommentar</label>
              <textarea value={freigabeText} onChange={(e) => setFreigabeText(e.target.value)} rows={2}
                className="w-full px-3 py-2 border border-line rounded-sm bg-white text-sm mb-4" />
              {err && <div className="text-sm mb-3" style={{ color: "#BE2E6C" }}>{err}</div>}
              <div className="flex items-center justify-end gap-2">
                <button onClick={() => setShowFreigabe(false)} className="px-3 py-2 text-sm hover:bg-line/50 rounded-sm">Abbrechen</button>
                <button onClick={submitFreigabe} disabled={busy || !autorName.trim()} data-testid="kf-confirm-freigabe"
                  className="inline-flex items-center gap-2 px-4 py-2 rounded-sm text-sm text-white disabled:opacity-50"
                  style={{ backgroundColor: "#157A4A" }}>
                  <CheckCircle2 size={13} /> Jetzt freigeben
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Kommentar-Verlauf */}
        <div className="paper-card p-6" data-testid="kf-kommentare-list">
          <div className="font-archivo font-bold text-lg mb-4">Verlauf</div>
          {(entwurf.kunden_kommentare || []).length === 0 && (
            <div className="text-sm text-ink/50">Noch keine Kommentare.</div>
          )}
          <div className="space-y-3">
            {(entwurf.kunden_kommentare || []).map((k) => (
              <div key={k.id} className="border-l-2 pl-3" style={{ borderLeftColor: k.quelle === "pm" ? "#0E62C4" : k.is_freigabe ? "#157A4A" : "#1B1E23" }}>
                <div className="mono-label" style={{ opacity: 1, color: k.quelle === "pm" ? "#0E62C4" : k.is_freigabe ? "#157A4A" : "#1B1E23" }}>
                  {k.autor_name} · {k.quelle === "pm" ? "PM" : "Kunde"}{k.is_freigabe ? " · Freigabe" : ""}
                </div>
                <div className="text-sm mt-1 whitespace-pre-wrap">{k.text}</div>
                <div className="mono-label mt-1">{fmtDate(k.erstellt_am)} · {fmtTime(k.erstellt_am)}</div>
              </div>
            ))}
          </div>
        </div>
      </main>

      <footer className="max-w-5xl mx-auto px-6 py-8 mono-label text-center" style={{ opacity: 0.6 }}>
        Brunner Werbegrafik · Freigabeportal
      </footer>
    </div>
  );
}
